import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import { AnimatedBackground } from '../../../containers/Results/View/ResultsList/Loading';
import { ISearchParameters } from '../../../contexts/Search/types';
import { formatRoundPrice, getRoundPrice } from '../../../helpers/results';
import windowSize from '../../../helpers/windowSize';
import { SearchIcon } from '../../../images/icons/search';
import { dayjs } from '../../../libs/day';
import { getPriceCalendarByMonth } from '../../../services/priceCalendar';
import { PriceCalendar } from '../../../services/priceCalendar/types';
import { colors } from '../../../styles/constants';
import DatePicker from '../../DatePicker';
import { DatePickerCalendarProps } from '../../DatePicker/DatePickerCalendar';
import { EDatePickerModifiedDate } from '../../DatePicker/types';
import { Select } from '../../Forms/Select';
import { PanelScrollableWrapper } from '../../Funnel/Panel/common';
import { MainPanel } from '../../Funnel/Panel/MainPanel';
import {
  DATE_TIME_SELECTOR_GAP,
  DATE_TIME_SELECTOR_MAX_TIME,
  DATE_TIME_SELECTOR_MIN_TIME,
  MAXIMUM_NUMBER_OF_MONTHS_IN_THE_FUTURE,
  TSB_DATE_TIME_SELECTOR_TEST_ID,
  TSB_DEPARTURE_DATE_PANEL_TEST_ID,
  TSB_RETURN_DATE_PANEL_TEST_ID,
} from '../constants';
import messages from '../messages';
import { ESearchPanel } from '.';
import { PanelCTAStyle } from './common';

const Wrapper = styled.div`
  ${tw`relative h-full flex flex-col`}
  gap:var(--size-medium)
`;

const TimePickerWrapper = styled.div`
  ${tw`flex justify-center`}
  gap:var(--size-medium);
`;

const TimePickerTitle = styled.span`
  ${tw`font-kyrial text-title-medium-m leading-title-medium-m lg:text-title-small lg:leading-title-small`}
`;

const DatePickerWrapper = styled(PanelScrollableWrapper)`
  ${tw`flex flex-col items-center h-full`}
`;

const PassengerLengthWrapper = styled.div`
  ${tw`w-full flex justify-end text-p-xsmall leading-p-xsmall md:hidden`}
  margin-top:var(--size-medium);
`;

function getTimes(min: number, max: number, gap: number) {
  const times = [];
  for (let i = min; i <= max; i += gap) {
    times.push(i);
  }

  return times;
}

const PriceWrapper = styled.span`
  ${tw`h-icon-18 w-full font-bold flex px-tiny`}
`;

const PriceTag = styled.span<{ isCheapest: boolean }>`
  ${tw`w-full text-p-xsmall-m leading-p-xsmall-m `}
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ isCheapest }) =>
    isCheapest && tw`bg-accent-yellow-400 rounded-15 text-text font-bold`}
`;

const LoadingPrice = styled(AnimatedBackground)`
  ${tw`block h-full w-full rounded-15 leading-p-xsmall-m`}
`;

interface DatesPanelProps {
  onClose: () => void;

  modifiedDate: EDatePickerModifiedDate;
  searchParameters: ISearchParameters;
  setOutboundDate: (date: string, from: number | null) => void;
  setReturnDate: (date: string, from: number | null) => void;
  focusInput: (input: ESearchPanel) => void;
  closePanelButtonRef: React.MutableRefObject<HTMLButtonElement>;
}

/**
 * @description Returns a component that displays a panel for modifying the travel dates.
 * @returns {React.FC<DatesPanelProps>}
 */
const DatesPanel: React.FC<DatesPanelProps> = ({
  closePanelButtonRef,
  focusInput,
  modifiedDate,
  onClose,
  searchParameters,
  setOutboundDate,
  setReturnDate,
}) => {
  const [initialSearchParameters] =
    useState<ISearchParameters>(searchParameters);

  const [calendarContext, setCalendarContext] = useState<{
    isLoading: boolean;
    calendar: PriceCalendar | null;
  }>({
    isLoading: false,
    calendar: null,
  });

  const availableTrips =
    calendarContext.calendar?.filter((date) => date.trip !== null) || [];
  const cheapestPrice = availableTrips.reduce((minPrice, trip) => {
    return Math.min(minPrice, trip.trip.priceCents);
  }, Infinity);

  const startDate = searchParameters.outboundDate
    ? dayjs(searchParameters.outboundDate)
    : dayjs();

  const [currentDisplayedMonth, setCurrentDisplayedMonth] =
    useState<dayjs.Dayjs>(startDate);

  const { destinationId, originId } = searchParameters;

  useEffect(() => {
    if (!originId || !destinationId || !currentDisplayedMonth) {
      return;
    }

    setCalendarContext({
      isLoading: true,
      calendar: null,
    });

    getPriceCalendarByMonth({
      originId:
        modifiedDate === EDatePickerModifiedDate.START
          ? originId.toString()
          : destinationId.toString(),
      destinationId:
        modifiedDate === EDatePickerModifiedDate.START
          ? destinationId.toString()
          : originId.toString(),
      month: currentDisplayedMonth.format('YYYY-MM'),
    })
      .then((calendar) => {
        setCalendarContext({
          isLoading: false,
          calendar,
        });
      })
      .catch(() => {
        setCalendarContext({
          isLoading: false,
          calendar: null,
        });
      });
  }, [currentDisplayedMonth, destinationId, modifiedDate, originId]);

  const renderPrice: DatePickerCalendarProps['renderDayExtraContent'] = (
    props,
  ) => {
    const { day, isDisabled } = props;

    if (isDisabled) {
      return <PriceWrapper></PriceWrapper>;
    }

    if (calendarContext.isLoading) {
      return (
        <PriceWrapper>
          <LoadingPrice />
        </PriceWrapper>
      );
    }

    const index = day.date() - 1;

    const trip = calendarContext.calendar
      ? calendarContext.calendar[index]?.trip
      : null;

    return (
      <PriceWrapper>
        <PriceTag isCheapest={trip ? cheapestPrice === trip.priceCents : false}>
          {trip ? (
            formatRoundPrice(getRoundPrice(trip.priceCents))
          ) : (
            <SearchIcon color={colors.primaryLight100} />
          )}
        </PriceTag>
      </PriceWrapper>
    );
  };

  function onTimeChange(time: number) {
    if (modifiedDate === EDatePickerModifiedDate.START) {
      setOutboundDate(searchParameters.outboundDate, time);
      return;
    }

    setReturnDate(searchParameters.returnDate, time);
  }

  function isTimeDisabled(time: number): boolean {
    if (
      (!searchParameters.returnDate && !searchParameters.outboundDate) ||
      searchParameters.returnDate !== searchParameters.outboundDate
    ) {
      return false;
    }

    if (modifiedDate === EDatePickerModifiedDate.START) {
      if (!searchParameters.returnTime) {
        return false;
      }

      return time >= searchParameters.returnTime;
    }

    if (!searchParameters.outboundTime) {
      return false;
    }

    return time <= searchParameters.outboundTime;
  }

  function onUpdateModifiedDate(type: EDatePickerModifiedDate) {
    if (type === EDatePickerModifiedDate.START) {
      focusInput(ESearchPanel.DEPARTURE_DATE);
    } else if (type === EDatePickerModifiedDate.END) {
      focusInput(ESearchPanel.RETURN_DATE);
    }
  }

  return (
    <MainPanel
      CTAButtonContent={<FormattedMessage {...messages.validate} />}
      CTAContainerStyle={PanelCTAStyle}
      closeButtonRef={closePanelButtonRef}
      onCTAButtonClick={() => closePanelButtonRef.current.click()}
      onClose={(event) => {
        // event.isTrusted is true when the event was generated by a user action, and false when the event was created or modified by a script or dispatched
        if (windowSize.smallSize() && event.isTrusted) {
          if (modifiedDate === EDatePickerModifiedDate.START) {
            setOutboundDate(
              initialSearchParameters.outboundDate,
              searchParameters.outboundTime,
            );
          } else {
            setReturnDate(
              initialSearchParameters.returnDate,
              initialSearchParameters.returnTime,
            );
          }
        }

        onClose();
      }}
      testid={
        modifiedDate === EDatePickerModifiedDate.START
          ? TSB_DEPARTURE_DATE_PANEL_TEST_ID
          : TSB_RETURN_DATE_PANEL_TEST_ID
      }
      title={
        modifiedDate === EDatePickerModifiedDate.START ? (
          <FormattedMessage {...messages.datesPanelDepartureOverlayTitle} />
        ) : (
          <FormattedMessage {...messages.datesPanelArrivalOverlayTitle} />
        )
      }
    >
      <Wrapper>
        <DatePickerWrapper>
          <DatePicker
            endDate={
              searchParameters.returnDate
                ? dayjs(searchParameters.returnDate)
                : null
            }
            footer={
              (modifiedDate === EDatePickerModifiedDate.START ||
                searchParameters.returnDate) && (
                <TimePicker
                  isDisabled={isTimeDisabled}
                  onChange={onTimeChange}
                  value={
                    modifiedDate === EDatePickerModifiedDate.START
                      ? searchParameters.outboundTime
                      : searchParameters.returnTime
                  }
                />
              )
            }
            isNextMonthDisabled={(date) =>
              date.isSameOrAfter(
                dayjs().add(MAXIMUM_NUMBER_OF_MONTHS_IN_THE_FUTURE, 'month'),
                'month',
              )
            }
            isOutOfRange={(date) => date.isBefore(dayjs(), 'day')}
            isPrevMonthDisabled={(date) => date.isSame(dayjs(), 'month')}
            modifiedDate={modifiedDate}
            onEndDateChange={(date) =>
              setReturnDate(
                date ? date.format('YYYY-MM-DD') : null,
                date ? searchParameters.returnTime : null,
              )
            }
            onMonthChange={setCurrentDisplayedMonth}
            onStartDateChange={(date) =>
              setOutboundDate(
                date ? date.format('YYYY-MM-DD') : null,
                date && modifiedDate === EDatePickerModifiedDate.START
                  ? searchParameters.outboundTime
                  : null,
              )
            }
            onUpdateModifiedDate={onUpdateModifiedDate}
            renderDayExtraContent={renderPrice}
            startDate={startDate}
          />

          <PassengerLengthWrapper>
            <FormattedMessage
              {...messages.passengersCount}
              values={{ count: searchParameters.passengers.length }}
            />
          </PassengerLengthWrapper>
        </DatePickerWrapper>
      </Wrapper>
    </MainPanel>
  );
};

interface TimePickerProps {
  value: number;
  onChange: (time: number | null) => void;
  isDisabled: (time: number) => boolean;
}

const TimePicker: React.FC<TimePickerProps> = ({
  isDisabled,
  onChange,
  value,
}) => (
  <TimePickerWrapper>
    <TimePickerTitle>
      <FormattedMessage {...messages.datesPanelTimePickerTitle} />
    </TimePickerTitle>

    <Select
      customStyle={css`
        height: max-content;
      `}
      data-testid={TSB_DATE_TIME_SELECTOR_TEST_ID}
      id={'tsb-select-start-time'}
      onChange={(event) => onChange(Number(event.target.value) || null)}
      value={value || undefined}
    >
      <option value={undefined}>- -</option>
      {getTimes(
        DATE_TIME_SELECTOR_MIN_TIME,
        DATE_TIME_SELECTOR_MAX_TIME,
        DATE_TIME_SELECTOR_GAP,
      ).map((time) => (
        <option key={time} disabled={isDisabled(time)} value={time}>
          {time}h
        </option>
      ))}
    </Select>
  </TimePickerWrapper>
);

export default DatesPanel;
